import {
  HiArrowDown,
  HiArrowLeft,
  HiArrowRight,
  HiArrowUp,
} from 'react-icons/hi'
import { useCurrentCanvasSide, useWidget } from '../EditorDataProvider'
import { IPatternWidget } from '@pageFeatures/design/onlineEditor/Widgets/PatternWidget'
import {
  EDITOR_CONFIG_FACTOR,
  calculateAssetSize,
  calculatePositionOffset,
  getMargins,
} from '@pageFeatures/design/onlineEditor/editor.config'
import { TCanvas } from '@pageFeatures/design/onlineEditor/Canvas'

const LIMITATION_POSITION_RATIO = 0.2
const LIMITATION_POSITION_LEFT_RATIO = LIMITATION_POSITION_RATIO
const LIMITATION_POSITION_RIGHT_RATIO = 0.5 + LIMITATION_POSITION_RATIO
const LIMITATION_POSITION_TOP_RATIO = 0.5 + LIMITATION_POSITION_RATIO
const LIMITATION_POSITION_BOTTOM_RATIO = 0.5 + LIMITATION_POSITION_RATIO

function getNextPositionSteps(widgetConfig: IPatternWidget): {
  left: number
  right: number
  top: number
  bottom: number
} {
  const currentVerticalStepCount = widgetConfig.verticalPositionStepCount
  const currentHorizontalStepCount = widgetConfig.horizontalPositionStepCount
  return {
    top: currentVerticalStepCount - 1,
    right: currentHorizontalStepCount + 1,
    bottom: currentVerticalStepCount + 1,
    left: currentHorizontalStepCount - 1,
  }
}

function getPositionStepBoundaryFence(
  canvasConfig: TCanvas,
  widgetConfig: IPatternWidget
): {
  left: number
  right: number
  top: number
  bottom: number
} {
  const initialSize =
    widgetConfig?.variant === 'one line pattern'
      ? Math.min(canvasConfig?.width, canvasConfig?.height) * 0.5
      : canvasConfig?.width * EDITOR_CONFIG_FACTOR.ASSET_SIZE_RATIO

  const { assetWidth, assetHeight } = calculateAssetSize(
    canvasConfig,
    widgetConfig?.scaleStepCount,
    {
      w: initialSize,
      h: initialSize,
    }
  )

  const { horizontalMargin, verticalMargin } = getMargins({
    assetWidth,
    assetHeight,
    horizontalMarginStepCount: widgetConfig.horizontalMarginStepCount,
    verticalMarginStepCount: widgetConfig.verticalMarginStepCount,
    patternVariant: widgetConfig?.variant,
    canvasHeight: canvasConfig?.height,
  })
  const colWidth = horizontalMargin + assetWidth
  const rowHeight = verticalMargin + assetHeight
  const positionOffset = calculatePositionOffset(canvasConfig.width)
  return {
    left:
      0 -
      Math.ceil((LIMITATION_POSITION_LEFT_RATIO * colWidth) / positionOffset),
    right: Math.ceil(
      (LIMITATION_POSITION_RIGHT_RATIO * colWidth) / positionOffset
    ),
    top:
      0 -
      Math.ceil((LIMITATION_POSITION_TOP_RATIO * rowHeight) / positionOffset),
    bottom: Math.ceil(
      (LIMITATION_POSITION_BOTTOM_RATIO * rowHeight) / positionOffset
    ),
  }
}

export function PositionOffsetButtons() {
  const { widget, updateWidget } = useWidget('pattern')
  const { canvasConfig } = useCurrentCanvasSide()
  const { config: widgetConfig } = widget
  const nextPositionSteps = getNextPositionSteps(widgetConfig)
  const positionStepBoundaryFence = getPositionStepBoundaryFence(
    canvasConfig,
    widgetConfig
  )
  const moveLeft = () => {
    if (nextPositionSteps.left < positionStepBoundaryFence.left) {
      return
    }
    updateWidget({
      horizontalPositionStepCount: nextPositionSteps.left,
    })
  }
  const moveRight = () => {
    if (nextPositionSteps.right > positionStepBoundaryFence.right) {
      return
    }
    updateWidget({
      horizontalPositionStepCount: nextPositionSteps.right,
    })
  }
  const moveUp = () => {
    if (nextPositionSteps.top < positionStepBoundaryFence.top) {
      return
    }
    updateWidget({
      verticalPositionStepCount: nextPositionSteps.top,
    })
  }
  const moveDown = () => {
    if (nextPositionSteps.bottom > positionStepBoundaryFence.bottom) {
      return
    }
    updateWidget({
      verticalPositionStepCount: nextPositionSteps.bottom,
    })
  }

  const arrowButtonClass =
    'flex items-center justify-center w-ful px-0 text-xl border-2 rounded-lg text-acai hover:cursor-pointer disabled:text-core-grey-60'

  return (
    <div className="grid grid-cols-7 grid-rows-2 gap-1">
      <button
        className={`${arrowButtonClass} col-span-2 row-span-2 hover:bg-black-10`}
        onClick={moveLeft}
        disabled={nextPositionSteps.left < positionStepBoundaryFence.left}
      >
        <HiArrowLeft />
      </button>
      <button
        className={`${arrowButtonClass} col-span-3 row-span-1 hover:bg-black-10`}
        onClick={moveUp}
        disabled={nextPositionSteps.top < positionStepBoundaryFence.top}
      >
        <HiArrowUp />
      </button>
      <button
        className={`${arrowButtonClass} col-span-2 row-span-2 hover:bg-black-10`}
        onClick={moveRight}
        disabled={nextPositionSteps.right > positionStepBoundaryFence.right}
      >
        <HiArrowRight />
      </button>
      <button
        className={`${arrowButtonClass} col-span-3 row-span-1 hover:bg-black-10`}
        onClick={moveDown}
        disabled={nextPositionSteps.bottom > positionStepBoundaryFence.bottom}
      >
        <HiArrowDown />
      </button>
    </div>
  )
}
